import { GithubWorkflow } from "projen/lib/github";

/**
 * Job id of the apply workflow's validation job. Every apply job depends on
 * it, so a failed validation skips the whole apply run.
 */
export const VALIDATE_PLAN_JOB_ID = "validate-plan";

/**
 * Name of the `workflow_dispatch` input carrying the plan run to apply.
 */
export const PLAN_RUN_ID_INPUT = "plan_run_id";

/**
 * Name of the artifact projen's build job uploads. The apply workflow
 * downloads this one cross-run.
 *
 * Mirrors projen's internal `BUILD_ARTIFACT_NAME`, which is not re-exported
 * from `projen/lib/github`.
 */
export const BUILD_ARTIFACT_NAME = "build-artifact";

/**
 * Permission-backup file projen writes beside the build artifact.
 *
 * Mirrors projen's internal `PERMISSION_BACKUP_FILE`, which is not
 * re-exported from `projen/lib/github`.
 */
export const PERMISSION_BACKUP_FILE = "permissions-backup.acl";

/**
 * Extra knobs for the `plan-apply` gate. Every field is optional and only read
 * when `gate` is `plan-apply`.
 */
export interface PlanApplyOptions {
  /**
   * Put this workflow's apply jobs on an apply workflow another
   * `AwsDeployWorkflow` already created, instead of creating a second one.
   *
   * Pass that component's `applyWorkflow`. Sharing is what lets a multi-service
   * repo render a cross-service `deployAfterTargets` graph: GitHub resolves
   * `needs` only within a single workflow file. Every component sharing an
   * apply workflow must share one build workflow and agree on
   * {@link requireCurrentHead}, {@link verifyArtifactDigest}, and
   * `homeRepository`, all of which the single shared validation job bakes in.
   *
   * Mutually exclusive with {@link applyWorkflowName}.
   *
   * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
   * @default - this component creates its own apply workflow
   */
  readonly applyWorkflow?: GithubWorkflow;

  /**
   * File name (without `.yml`) of the generated apply workflow.
   *
   * @default - the plan workflow's name suffixed with `-apply`
   */
  readonly applyWorkflowName?: string;

  /**
   * Also assert the plan run's `head_sha` still equals the current head of the
   * branch it ran on, so a plan can only be applied while it is still the tip
   * of its branch.
   *
   * Off by default: a legitimate approval window is exactly the time in which
   * someone may push an unrelated commit, and failing every such apply is
   * usually more disruptive than the drift it prevents.
   *
   * @default false
   */
  readonly requireCurrentHead?: boolean;

  /**
   * Resolve the plan run's build-artifact digest during validation and pass it
   * to `actions/download-artifact` as `artifact-digest`, so a download whose
   * content does not match is rejected.
   *
   * Fails closed when the API reports no digest for the artifact.
   *
   * @default false
   */
  readonly verifyArtifactDigest?: boolean;
}

/**
 * Inputs to {@link renderPlanValidationScript}.
 */
export interface PlanValidationScriptOptions {
  /**
   * Repo-relative path of the plan workflow file the run must belong to.
   */
  readonly planWorkflowPath: string;

  /**
   * Branch patterns the plan run is allowed to have run on. Exact names and
   * trailing globs (`feature/*`) are both supported.
   */
  readonly allowedBranches: Array<string>;

  /**
   * Assert the plan run's commit is still the branch head.
   */
  readonly requireCurrentHead: boolean;

  /**
   * Resolve and export the build artifact's digest.
   */
  readonly verifyArtifactDigest: boolean;
}

/**
 * Render the `actions/github-script` body that validates a plan run before any
 * apply job assumes a deploy role.
 *
 * Every check fails closed — the script `setFailed`s and returns rather than
 * falling through — because everything downstream of it deploys to a real
 * account. The run id arrives via `process.env.PLAN_RUN_ID` rather than a
 * `${{ }}` interpolation so a crafted dispatch input cannot inject JavaScript
 * into this script.
 *
 * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
 */
export const renderPlanValidationScript = (
  options: PlanValidationScriptOptions,
): string => {
  const {
    planWorkflowPath,
    allowedBranches,
    requireCurrentHead,
    verifyArtifactDigest,
  } = options;

  return [
    "const raw = (process.env.PLAN_RUN_ID ?? '').trim();",
    "const runId = Number(raw);",
    "if (!/^[0-9]+$/.test(raw) || !Number.isSafeInteger(runId) || runId <= 0) {",
    '  core.setFailed(`plan_run_id must be a positive run id, got "${raw}"`);',
    "  return;",
    "}",
    "",
    "const { owner, repo } = context.repo;",
    "const { data: run } = await github.rest.actions.getWorkflowRun({",
    "  owner,",
    "  repo,",
    "  run_id: runId,",
    "});",
    "",
    `const expectedPath = ${JSON.stringify(planWorkflowPath)};`,
    "if (run.path !== expectedPath) {",
    '  core.setFailed(`Run ${runId} belongs to workflow "${run.path}", expected "${expectedPath}".`);',
    "  return;",
    "}",
    "",
    'if (run.status !== "completed" || run.conclusion !== "success") {',
    '  core.setFailed(`Run ${runId} is status "${run.status}" / conclusion "${run.conclusion}", expected a completed successful plan run.`);',
    "  return;",
    "}",
    "",
    `const allowedBranches = ${JSON.stringify(allowedBranches)};`,
    'const branch = run.head_branch ?? "";',
    "const branchAllowed = allowedBranches.some((pattern) =>",
    '  pattern.includes("*")',
    '    ? branch.startsWith(pattern.slice(0, pattern.indexOf("*")))',
    "    : branch === pattern,",
    ");",
    "if (!branchAllowed) {",
    '  core.setFailed(`Run ${runId} ran on branch "${branch}", which no deployment target in this workflow accepts (allowed: ${allowedBranches.join(", ")}).`);',
    "  return;",
    "}",
    ...(requireCurrentHead
      ? [
          "",
          "const { data: liveBranch } = await github.rest.repos.getBranch({",
          "  owner,",
          "  repo,",
          "  branch,",
          "});",
          "if (liveBranch.commit.sha !== run.head_sha) {",
          '  core.setFailed(`Run ${runId} planned ${run.head_sha} but "${branch}" now points at ${liveBranch.commit.sha}. Re-run the plan workflow against the current head.`);',
          "  return;",
          "}",
        ]
      : []),
    "",
    "const artifacts = await github.paginate(",
    "  github.rest.actions.listWorkflowRunArtifacts,",
    "  { owner, repo, run_id: runId, per_page: 100 },",
    ");",
    `const artifactName = ${JSON.stringify(BUILD_ARTIFACT_NAME)};`,
    "const artifact = artifacts.find((a) => a.name === artifactName);",
    "if (!artifact) {",
    '  core.setFailed(`Run ${runId} published no "${artifactName}" artifact. Only a plan run whose build job completed can be applied.`);',
    "  return;",
    "}",
    "if (artifact.expired) {",
    '  core.setFailed(`The "${artifactName}" artifact on run ${runId} expired at ${artifact.expires_at}. Artifact retention is this gate\'s approval window — re-run the plan workflow.`);',
    "  return;",
    "}",
    ...(verifyArtifactDigest
      ? [
          "if (!artifact.digest) {",
          '  core.setFailed(`The "${artifactName}" artifact on run ${runId} reports no digest, so it cannot be verified. Disable planApply.verifyArtifactDigest or re-run the plan workflow.`);',
          "  return;",
          "}",
          'core.setOutput("artifact_digest", artifact.digest);',
        ]
      : []),
    "",
    'core.setOutput("head_sha", run.head_sha);',
    'core.setOutput("head_branch", branch);',
    "await core.summary",
    "  .addHeading(`Applying plan run ${runId}`, 2)",
    "  .addTable([",
    '    [{ data: "Field", header: true }, { data: "Value", header: true }],',
    '    ["Plan run", `<a href="${run.html_url}">${runId}</a>`],',
    '    ["Branch", branch],',
    '    ["Commit", run.head_sha],',
    '    ["Artifact expires", artifact.expires_at ?? "unknown"],',
    "  ])",
    "  .write();",
  ].join("\n");
};
